import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import firebase from 'firebase/app';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Cloud Functions that check the caller's Firebase ID token (HTTP functions, stage B of the security work).
 * Add a name here when its server side starts to require `Authorization: Bearer <ID token>`.
 * Callable functions (`httpsCallable`) send the token on their own and do not need to be listed.
 */
export const FUNCTIONS_THAT_REQUIRE_ID_TOKEN: string[] = [
  'admin-createAppUserV2',
  'email-sendSimpleEmail',
  'sms-sendSMS',
  'clio-createClioContacts',
  'clio-createClioEvent',
  'clio-createWebHooksV5',
  'clio-getAuthorizationCodeV8',
  'clio-registerClioCustomActionsV1',
  'clio-downloadDocumentsV2',
  'email-sendEmail',
  'email-simpleMail',
  'email-simpleEmail',
  'email-simpleEmailV3',
  'healthcareApiV8',
  'admin-getPlansV2',
];

/** True when `url` is `<baseUrl><name>` (optionally followed by a query string or hash) for a listed function. */
export function needsIdToken(url: string, baseUrl: string, names: string[] = FUNCTIONS_THAT_REQUIRE_ID_TOKEN): boolean {
  if (!url || !baseUrl || !url.startsWith(baseUrl)) {
    return false;
  }
  const rest = url.substring(baseUrl.length);
  const name = rest.split(/[?#/]/)[0];
  return names.includes(name);
}

/**
 * Adds the signed-in user's ID token to calls to the listed Cloud Functions. Other requests are not touched.
 * If nobody is signed in the request goes out unchanged and the server answers 401.
 */
@Injectable()
export class FunctionAuthInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const baseUrl = environment.constants.cloudfunctionsURL;
    const user = firebase.auth().currentUser;
    if (!user || !needsIdToken(req.url, baseUrl)) {
      return next.handle(req);
    }
    return from(user.getIdToken()).pipe(
      switchMap(token => next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
    );
  }
}
